import type { HeaderNavAudienceApiParam } from "@/react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";
import type { Params } from "../toQueryString";
import { toQueryString } from "../toQueryString";
import { absolutePath } from "./absolutePath";

interface SettingsFnProps {
  id?: string | number;
  options?: Record<string, unknown>;
  params?: Record<string, unknown> | object;
  uuid?: string | number;
}

interface AudienceParams {
  audience?: HeaderNavAudienceApiParam;
}

interface CollectionParams {
  params?: Params;
}

interface ItemParams {
  pageId: string | number;
  params?: Params;
}

interface RevisionParams {
  pageId: string | number;
  params?: Params;
  revisionId: string | number;
}

export const settings = {
  home: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("../settings/home", params),
    update: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("../settings/home", params),
  },
  landingPage: {
    welcomeBanner: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath(
          "../settings/landing_page/welcome_banner_preference",
          params,
        ),
    },
  },
  attachedFiles: {
    destroy: () => absolutePath("../settings/communities/attached_files"),
  },
  openGraphImage: {
    destroy: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`../opengraph_images/${uuid}`, params),
  },
  courses: {
    lessons: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/courses/lessons", params),
      show: ({ id, params = {} }: SettingsFnProps = {}) =>
        absolutePath(`settings/courses/lessons/${id}`, params),
    },
    quizzes: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/courses/quizzes", params),
    },
    sections: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/courses/sections", params),
      show: ({ id, params = {} }: SettingsFnProps = {}) =>
        absolutePath(`settings/courses/sections/${id}`, params),
    },
  },
  posts: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/posts", params),
  },
  analytics: {
    analyticsIframe: ({ params = {}, options = {} }: SettingsFnProps) =>
      absolutePath("settings/analytics_iframe", params, options),
    analyticsExport: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/analytics_exports", params),
  },
  mobileApp: {
    show: () => absolutePath("settings/mobile_app"),
    update: () => absolutePath("settings/mobile_app"),
  },
  mobileHomeSegments: {
    update: (id: string | number) =>
      absolutePath(`settings/mobile_home_segments/${id}`),
  },
  memberTags: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/member_tags", params),
    show: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/member_tags/${uuid}`, params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/member_tags", params),
    update: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/member_tags/${uuid}`, params),
    destroy: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/member_tags/${uuid}`, params),
  },
  postBulkActions: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("posts/bulk_actions", params),
    show: ({ id, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`posts/bulk_actions/${id}`, params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("posts/bulk_actions", params),
  },
  topics: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/topics", params),
    show: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/topics/${uuid}`, params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/topics", params),
    update: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/topics/${uuid}`, params),
    destroy: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/topics/${uuid}`, params),
  },
  api: {
    index: ({ params = {} } = {}) => absolutePath("settings/api", params),
    revoke: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/api/${uuid}/revoke`, params),
  },
  activityLogs: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/activity_logs", params),
    exportCsv: (params: Record<string, unknown> | Params = {}) =>
      `/settings/members/activity_logs/export.csv?${toQueryString(params)}`,
    entityPath: ({ id }: SettingsFnProps = {}) =>
      `/settings/members/activity_logs/${id}/entity_path`,
    sourcePath: ({ id }: SettingsFnProps = {}) =>
      `/settings/members/activity_logs/${id}/source_path`,
  },
  v3_ui_preference: {
    update: () => "/settings/v3",
  },
  legal: {
    show: () => absolutePath(`settings/legal`),
    update: () => absolutePath(`settings/legal`),
  },
  navigation: {
    list: ({ params = {} }: { params?: AudienceParams } = {}) =>
      absolutePath(`settings/header_menu_links`, params),
    reorder: ({ params = {} }: { params?: AudienceParams } = {}) =>
      absolutePath(`settings/header_menu_links/reorder`, params),
    create: () => absolutePath(`settings/header_menu_links`),
    edit: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/header_menu_links/${id}`),
    show: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/header_menu_links/${id}`),
    destroy: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/header_menu_links/${id}`),
  },
  storage: {
    metrics: {
      show: () => absolutePath("settings/storage/metrics"),
    },
    files: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/storage/files", params),
      show: ({ id, params = {} }: SettingsFnProps & { id: string | number }) =>
        absolutePath(`settings/storage/files/${id}`, params),
      destroy: ({
        id,
        params = {},
      }: SettingsFnProps & { id: string | number }) =>
        absolutePath(`settings/storage/files/${id}`, params),
    },
  },
  sites: {
    settings: {
      show: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/site/settings", params),
      update: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/site/settings", params),
    },
    pages: {
      index: ({ params = {} }: CollectionParams = {}) =>
        absolutePath("settings/site/pages", params),
      create: ({ params = {} }: CollectionParams = {}) =>
        absolutePath("settings/site/pages", params),
      destroy: ({ pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}`),
      show: ({ params = {}, pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}`, params),
      update: ({ params = {}, pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}`, params),
      updateSettings: ({ params = {}, pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}/settings`, params),
      publish: ({ pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}/publish`),
      unpublish: ({ pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}/unpublish`),
      duplicate: ({ pageId }: ItemParams) =>
        absolutePath(`settings/site/pages/${pageId}/duplicate`),
      revisions: {
        index: ({ params = {}, pageId }: Omit<RevisionParams, "revisionId">) =>
          absolutePath(`settings/site/pages/${pageId}/revisions`, params),
        show: ({ params = {}, pageId, revisionId }: RevisionParams) =>
          absolutePath(
            `settings/site/pages/${pageId}/revisions/${revisionId}`,
            params,
          ),
        restore: ({ params = {}, pageId, revisionId }: RevisionParams) =>
          absolutePath(
            `settings/site/pages/${pageId}/revisions/${revisionId}/restore`,
            params,
          ),
      },
      themes: {
        update: ({ pageId }: { pageId: number }) =>
          absolutePath(`settings/site/pages/${pageId}/change_theme`),
      },
      custom_fonts: {
        index: ({ pageId }: { pageId: number }) =>
          absolutePath(`settings/site/pages/${pageId}/custom_fonts`),
        show: ({ pageId, id }: { pageId: number; id: number }) =>
          absolutePath(`settings/site/pages/${pageId}/custom_fonts/${id}`),
        create: ({ pageId }: { pageId: number }) =>
          absolutePath(`settings/site/pages/${pageId}/custom_fonts`),
        destroy: ({ pageId, id }: { pageId: number; id: number }) =>
          absolutePath(`settings/site/pages/${pageId}/custom_fonts/${id}`),
      },
    },
    themes: {
      index: () => absolutePath(`settings/site/themes`),
      update: ({ themeId }: { themeId: number }) =>
        absolutePath(`settings/site/themes/${themeId}`),
      create_custom_theme: () =>
        absolutePath("settings/site/themes/create_custom_theme"),
      delete: ({ themeId }: { themeId: number }) =>
        absolutePath(`settings/site/themes/${themeId}`),
      show: ({ themeId }: { themeId: number }) =>
        absolutePath(`settings/site/themes/${themeId}`),
    },
    redirects: {
      index: ({ params = {} }: CollectionParams = {}) =>
        absolutePath("settings/site/redirects", params),
      create: ({ params = {} }: CollectionParams = {}) =>
        absolutePath("settings/site/redirects", params),
      destroy: ({ redirectId }: { redirectId: string | number }) =>
        absolutePath(`settings/site/redirects/${redirectId}`),
      update: ({
        params = {},
        redirectId,
      }: {
        params?: Params;
        redirectId: string | number;
      }) => absolutePath(`settings/site/redirects/${redirectId}`, params),
    },
    reusableBlocks: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/reusable_blocks", params),
      create: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/reusable_blocks", params),
      show: ({ id, params = {} }: SettingsFnProps = {}) =>
        absolutePath(`settings/reusable_blocks/${id}`, params),
      update: ({ id, params = {} }: SettingsFnProps = {}) =>
        absolutePath(`settings/reusable_blocks/${id}`, params),
      destroy: ({ id }: SettingsFnProps = {}) =>
        absolutePath(`settings/reusable_blocks/${id}`),
    },
  },
};
